import { Component, ElementRef, Inject, ViewChild, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  Annotation,
  Point,
  Rect,
  renderAnnotated,
} from '../../services/screenshot-capture';

export interface ScreenshotEditorData {
  readonly canvas: HTMLCanvasElement;
  readonly annotations: readonly Annotation[];
}

/** Redact removes information; draw and text add it. */
export type Tool = 'redact' | 'draw' | 'text';

/**
 * A full-size editor for a captured screenshot.
 *
 * Separate from the report dialog on purpose. Annotating inside that dialog meant drawing on an
 * image squeezed into a 560px column, which is unusable for pointing at anything small, and the
 * report form had to scroll away to make room. Here the image gets the whole window.
 *
 * It edits a list of annotations, never the capture itself, so the original is always intact and
 * every annotation stays undoable. The caller gets the list back and re-renders from it.
 */
@Component({
  selector: 'app-screenshot-editor-dialog',
  templateUrl: './screenshot-editor-dialog.component.html',
  standalone: false,
})
export class ScreenshotEditorDialogComponent {
  readonly tool = signal<Tool>('draw');
  readonly annotations = signal<Annotation[]>([]);
  readonly preview = signal<string>('');

  /** Where a text annotation is being typed, in image pixels. Undefined when not typing. */
  readonly textAt = signal<Point | undefined>(undefined);
  readonly textValue = signal('');

  @ViewChild('previewimg') previewimg?: ElementRef<HTMLImageElement>;

  private dragStart?: Point;
  private drawing?: Point[];

  constructor(
    readonly dialogRef: MatDialogRef<ScreenshotEditorDialogComponent, Annotation[] | undefined>,
    @Inject(MAT_DIALOG_DATA) private readonly data: ScreenshotEditorData,
  ) {
    this.annotations.set([...data.annotations]);
    this.refreshPreview();
  }

  get width(): number {
    return this.data.canvas.width;
  }

  get height(): number {
    return this.data.canvas.height;
  }

  setTool(tool: Tool): void {
    this.tool.set(tool);
    this.cancelText();
  }

  undo(): void {
    this.annotations.update((all) => all.slice(0, -1));
    this.refreshPreview();
  }

  clear(): void {
    this.annotations.set([]);
    this.cancelText();
    this.refreshPreview();
  }

  /**
   * Named for the button. Commits any note still being typed first, so a reporter who types a
   * label and goes straight for Save does not silently lose it.
   */
  save(): void {
    this.commitText();
    this.dialogRef.close(this.annotations());
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }

  commitText(): void {
    const at = this.textAt();
    const text = this.textValue().trim();
    if (at && text) this.addAnnotation({ kind: 'text', at, text });
    this.cancelText();
  }

  cancelText(): void {
    this.textAt.set(undefined);
    this.textValue.set('');
  }

  /**
   * The image is shown at whatever size the window allows, so every pointer position has to be
   * converted back into the image's own pixels before it becomes an annotation.
   */
  onPointerDown(event: PointerEvent): void {
    const at = this.toImagePoint(event);

    if (this.tool() === 'text') {
      // A second click while typing commits what is there and starts a new note, which is what
      // anyone who has used an annotation tool expects.
      this.commitText();
      this.textAt.set(at);
      this.textValue.set('');
      return;
    }

    (event.target as Element).setPointerCapture?.(event.pointerId);
    this.dragStart = at;
    this.drawing = this.tool() === 'draw' ? [at] : undefined;
  }

  /** Live preview while dragging, so the reporter sees what they are covering or drawing. */
  onPointerMove(event: PointerEvent): void {
    if (!this.dragStart) return;
    const at = this.toImagePoint(event);

    if (this.drawing) {
      this.drawing.push(at);
      this.refreshPreview({ kind: 'draw', points: [...this.drawing] });
      return;
    }
    this.refreshPreview({ kind: 'redact', rect: rectBetween(this.dragStart, at) });
  }

  onPointerUp(event: PointerEvent): void {
    const start = this.dragStart;
    const points = this.drawing;
    this.dragStart = undefined;
    this.drawing = undefined;
    if (!start) return;

    const end = this.toImagePoint(event);

    if (points) {
      points.push(end);
      // A tap is not a stroke.
      if (points.length < 2) return this.refreshPreview();
      return this.addAnnotation({ kind: 'draw', points });
    }

    const rect = rectBetween(start, end);
    // A click without a drag is not a redaction, and a one-pixel box would only confuse.
    if (rect.width < 4 || rect.height < 4) return this.refreshPreview();
    this.addAnnotation({ kind: 'redact', rect });
  }

  private addAnnotation(annotation: Annotation): void {
    this.annotations.update((all) => [...all, annotation]);
    this.refreshPreview();
  }

  /** Always re-renders from the untouched capture, so nothing accumulates drift. */
  private refreshPreview(extra?: Annotation): void {
    const all = extra ? [...this.annotations(), extra] : this.annotations();
    this.preview.set(renderAnnotated(this.data.canvas, all).toDataURL('image/png'));
  }

  private toImagePoint(event: PointerEvent): Point {
    const image = this.previewimg?.nativeElement;
    if (!image) return { x: 0, y: 0 };
    const bounds = image.getBoundingClientRect();
    return {
      x: ((event.clientX - bounds.left) / bounds.width) * this.width,
      y: ((event.clientY - bounds.top) / bounds.height) * this.height,
    };
  }
}

function rectBetween(a: Point, b: Point): Rect {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    width: Math.abs(b.x - a.x),
    height: Math.abs(b.y - a.y),
  };
}
